/**
 * Derive a default requisitioner name from a customer's display name.
 *
 * Billing wants the requisitioner stored as "Last, First" (matching how the
 * field appears on the QuickBooks sales form). When a customer is selected and
 * the requisitioner is still blank, we seed it from the customer name so the
 * common case (the requisitioner IS the bill-to contact) needs no typing — but
 * a user-entered value is never clobbered (the caller guards on blank).
 *
 * Rules:
 *  - Already contains a comma -> assume it's already "Last, First"; return as-is.
 *  - "First Last" (2 tokens) -> "Last, First".
 *  - "First Middle Last" (3+ tokens) -> last token is the last name, the rest
 *    are first/middle -> "Last, First Middle".
 *  - Trailing generational suffix (Jr/Sr/II/III/IV/V, optional period, any case)
 *    folds onto the last name -> "John Smith Jr" -> "Smith Jr, John".
 *  - Single token / company-like (one word) -> return as-is.
 *  - Empty / whitespace -> return ''.
 */
// Generational name suffixes that belong with the last name, not as a standalone
// "last token". Matched case-insensitively, with an optional trailing period.
const NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v']);
function isNameSuffix(token) {
    return NAME_SUFFIXES.has(token.toLowerCase().replace(/\.$/, ''));
}
export function deriveRequisitioner(customerName) {
    if (!customerName)
        return '';
    const trimmed = customerName.trim();
    if (!trimmed)
        return '';
    // Already "Last, First" (or any comma-separated form) — leave untouched.
    if (trimmed.includes(','))
        return trimmed;
    const tokens = trimmed.split(/\s+/);
    // Single token (e.g. a company name like "Devon" or "Permian") — no reliable
    // first/last split, so return it verbatim.
    if (tokens.length < 2)
        return trimmed;
    // If the final token is a generational suffix, fold it onto the last name so
    // "John Smith Jr" -> "Smith Jr, John" rather than the wrong "Jr, John Smith".
    let lastNameCount = 1;
    if (tokens.length >= 3 && isNameSuffix(tokens[tokens.length - 1])) {
        lastNameCount = 2;
    }
    // Need at least one token left for the first/middle portion after pulling the
    // last name (+ optional suffix). Otherwise treat the whole thing as-is.
    if (tokens.length <= lastNameCount)
        return trimmed;
    const last = tokens.slice(tokens.length - lastNameCount).join(' ');
    const rest = tokens.slice(0, tokens.length - lastNameCount).join(' ');
    return `${last}, ${rest}`;
}
//# sourceMappingURL=requisitioner.js.map